import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useMsal } from '@azure/msal-react';
import { getTestsSummary, getProjectById, getTestType, getRemediationTrajectory } from '../Services/PtaasApiService';
import { LoaderIcon } from '../Components/Global/Icons';
import { FadeAlert } from '../Components/Global/Transitions';
import Tests from '../Models/Tests';
import Finding from '../Models/Finding';
import { ProjectRemediationSeries } from '../Models/Retest';

import { ALLOWED_STATUSES, SEV_COLORS } from '../Components/Dashboard/constants';
import { FindingWithProject, ProjectBucket } from '../Components/Dashboard/types';

import DashboardHeader           from '../Components/Dashboard/DashboardHeader';
import KpiCard                   from '../Components/Dashboard/KpiCard';
import SeverityDonut             from '../Components/Dashboard/SeverityDonut';
import FindingDistribution       from '../Components/Dashboard/FindingDistribution';
import AttackClassChart          from '../Components/Dashboard/AttackClassChart';
import ProjectTypesChart         from '../Components/Dashboard/ProjectTypesChart';
import Top10Table                from '../Components/Dashboard/Top10Table';
//import RemediationTrajectoryChart from '../Components/Dashboard/RemediationTrajectoryChart';
import TotalFindingsChart          from '../Components/Dashboard/TotalFindingsChart';
import SeverityLinesChart          from '../Components/Dashboard/SeverityLinesChart';
import FindingsPerTestChart        from '../Components/Dashboard/FindingsPerTestChart';
//import RecommendedActions   from '../Components/Dashboard/RecommendedActions';

/* ── helpers ─────────────────────────────────────────────────────────────── */

const toggleValue = (prev: string[], value: string): string[] =>
  prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value];

/* ── page ─────────────────────────────────────────────────────────────────── */

const DashboardPage: React.FC = () => {
  const { instance, accounts } = useMsal();

  // raw server data
  const [loading, setLoading]                       = useState(true);
  const [fetchError, setFetchError]                 = useState(false);
  const [tests, setTests]                           = useState<Tests[]>([]);
  const [projectBuckets, setProjectBuckets]         = useState<ProjectBucket[]>([]);
  const [testTypeNameMap, setTestTypeNameMap]       = useState<Record<string, string>>({});
  const [trajectorySeries, setTrajectorySeries]     = useState<ProjectRemediationSeries[]>([]);

  // active filters
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);

  /* ── data fetching ──────────────────────────────────────────────────────── */

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [returnedTests, testTypes, trajectory] = await Promise.all([
        getTestsSummary(instance, accounts[0]),
        getTestType(instance, accounts[0]),
        // No projectId param -- the project set is derived server-side from the
        // caller's ClientId (spec §9.2), so this is a single independent call.
        getRemediationTrajectory(instance, accounts[0]),
      ]);
      setTests(returnedTests);
      setTrajectorySeries(trajectory);

      // Build id → testTypeName lookup
      const nameMap: Record<string, string> = {};
      testTypes.forEach(tt => { nameMap[tt.id] = tt.testTypeName; });
      setTestTypeNameMap(nameMap);

      const relevantTests = returnedTests.filter(t => ALLOWED_STATUSES.includes(t.status));

      // step 1: canopy IDs now arrive on the enriched list — no per-test getProjectByTestId
      // fan-out (the old first N). The findings loop below still needs per-project Canopy data.
      const projectIdMap: Record<string, number | null> = {};
      relevantTests.forEach(test => { projectIdMap[test.id] = test.canopyProjectId ?? null; });

      // step 2: load full project data (phases + findings) in parallel
      const buckets: ProjectBucket[] = [];
      await Promise.all(
        relevantTests.map(async test => {
          const canopyId = projectIdMap[test.id];
          if (!canopyId) return;
          try {
            const full = await getProjectById(test.id, canopyId, instance, accounts[0]);
            const findings: Finding[] = [];
            full?.phases?.forEach(phase => phase.findings?.forEach(f => findings.push(f)));
            if (findings.length > 0) {
              buckets.push({ projectName: test.projectName, testId: test.id, canopyProjectId: canopyId, testTypes: test.testType ?? [], findings });
            }
          } catch {
            // project not yet available — skip silently
          }
        })
      );

      setProjectBuckets(buckets);
    } catch {
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ── filter option lists (derived from raw data) ────────────────────────── */

  const allProjectNames = useMemo(
    () => [...new Set(projectBuckets.map(b => b.projectName))].sort(),
    [projectBuckets]
  );

  /* ── filtered views ─────────────────────────────────────────────────────── */

  const filteredBuckets = useMemo((): ProjectBucket[] =>
    projectBuckets.filter(b =>
      selectedProjects.length === 0 || selectedProjects.includes(b.projectName)
    ),
    [projectBuckets, selectedProjects]
  );

  const filteredFindings = useMemo((): FindingWithProject[] =>
    filteredBuckets.flatMap(b =>
      b.findings.map(f => ({ ...f, projectName: b.projectName, testId: b.testId, canopyProjectId: b.canopyProjectId, testTypes: b.testTypes }))
    ),
    [filteredBuckets]
  );

  // The trajectory series carries its own projectName (independent of `projectBuckets`,
  // which only contains projects with *current* findings), so it's filtered directly
  // against the same selectedProjects state every other chart on this page uses.
  const filteredTrajectorySeries = useMemo((): ProjectRemediationSeries[] =>
    trajectorySeries.filter(s =>
      selectedProjects.length === 0 || selectedProjects.includes(s.projectName)
    ),
    [trajectorySeries, selectedProjects]
  );

  /* ── aggregate metrics ──────────────────────────────────────────────────── */

  const critical = filteredFindings.filter(f => f.rating === 1).length;
  const high     = filteredFindings.filter(f => f.rating === 2).length;
  const medium   = filteredFindings.filter(f => f.rating === 3).length;
  const low      = filteredFindings.filter(f => f.rating === 4).length;
  const info     = filteredFindings.filter(f => f.rating === 5).length;

  const cvssScores = filteredFindings.filter(f => f.cvss3_score != null).map(f => f.cvss3_score!);
  const avgCvss = cvssScores.length > 0
    ? (cvssScores.reduce((a, b) => a + b, 0) / cvssScores.length).toFixed(1)
    : '—';

  const attackClasses: [string, number][] = useMemo(() => {
    const m: Record<string, number> = {};
    filteredFindings.forEach(f => {
      if (f.attack_class__type) m[f.attack_class__type] = (m[f.attack_class__type] ?? 0) + 1;
    });
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  }, [filteredFindings]);

  const testTypeMap: Record<string, number> = useMemo(() => {
    const m: Record<string, number> = {};
    const visible = new Set(filteredBuckets.map(b => b.projectName));
    tests.forEach(t => {
      if (visible.has(t.projectName) || filteredBuckets.length === 0) {
        t.testType?.forEach(id => {
          if (!id) return;
          const name = testTypeNameMap[id] ?? id; // fall back to raw id if lookup missing
          m[name] = (m[name] ?? 0) + 1;
        });
      }
    });
    return m;
  }, [tests, filteredBuckets, testTypeNameMap]);

  const singleProjectRoute = useMemo(() => {
    if (filteredBuckets.length !== 1) return null;
    const b = filteredBuckets[0];
    if (!b.canopyProjectId) return null;
    const phase = b.findings[0]?.phase;
    if (phase == null) return null;
    return `/projects/${b.testId}/${b.canopyProjectId}/reports/${phase}`;
  }, [filteredBuckets]);

  /* ── recommended actions ─────────────────────────────────────────────────── */

  // const recommendations: RecommendedAction[] = [
  //   {
  //     urgency: 'Immediate',
  //     urgencyColor: SEV_COLORS.Critical,
  //     title: `Remediate ${Math.min(critical, 3)} Critical CVEs`,
  //     desc: 'Assign owners with 72-hr SLA.',
  //   },
  //   {
  //     urgency: 'This sprint',
  //     urgencyColor: TEAL,
  //     title: `Address ${critical} Critical findings`,
  //     desc: `All Critical findings across ${filteredBuckets.length} projects need remediation plans.`,
  //   },
  //   {
  //     urgency: 'This quarter',
  //     urgencyColor: TEAL,
  //     title: 'Reduce High backlog',
  //     desc: `${high} High open — target 80% closure.`,
  //   },
  //   {
  //     urgency: 'Ongoing',
  //     urgencyColor: '#179F8E',
  //     title: 'Expand pentest coverage',
  //     desc: 'Formalise threat modelling.',
  //   },
  // ];

  /* ── render ──────────────────────────────────────────────────────────────── */

  if (loading) return <LoaderIcon />;

  return (
    <div>
      <DashboardHeader
        projectNames={allProjectNames}
        selectedProjects={selectedProjects}
        onToggleProject={v => setSelectedProjects(p => toggleValue(p, v))}
        onClearProjects={() => setSelectedProjects([])}
      />

      <FadeAlert show={fetchError} dismissFunction={() => setFetchError(false)} />

      {/* KPI row */}
      <Row className="g-3 mb-3">
        <Col md={3}>
          <KpiCard
            value={filteredFindings.length}
            label="Total findings"
            sub={`across ${filteredBuckets.length} projects`}
          />
        </Col>
        <Col md={3}>
          <KpiCard
            value={critical}
            label="Critical"
            sub="immediate action required"
            accentColor={SEV_COLORS.Critical}
          />
        </Col>
        <Col md={3}>
          <KpiCard
            value={high}
            label="High severity"
            sub="remediation due"
            accentColor={SEV_COLORS.High}
          />
        </Col>
        <Col md={3}>
          <KpiCard value={avgCvss} label="Avg CVSS score" sub="weighted across all findings" />
        </Col>
      </Row>

      {/* Charts row */}
      <Row className="g-3 mb-3">
        <Col md={3}>
          <SeverityDonut critical={critical} high={high} medium={medium} low={low} info={info} />
        </Col>
        <Col md={6}>
          <FindingDistribution buckets={filteredBuckets} />
        </Col>
        <Col md={3}>
          <AttackClassChart data={attackClasses} />
        </Col>
      </Row>

      {/* Project types + Top 10 */}
      <Row className="g-3 mb-3">
        <Col md={3}>
          <ProjectTypesChart data={testTypeMap} reportPath={singleProjectRoute} />
        </Col>
        <Col md={9}>
          <Top10Table findings={filteredFindings} />
        </Col>
      </Row>

      {/* Remediation trajectory — the headline total leads, then the two ways of reading it */}
      <Row className="g-3 mb-3">
        <Col md={12}>
          <TotalFindingsChart series={filteredTrajectorySeries} />
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col md={7}>
          <SeverityLinesChart series={filteredTrajectorySeries} />
        </Col>
        <Col md={5}>
          <FindingsPerTestChart series={filteredTrajectorySeries} />
        </Col>
      </Row>

      {/* Per-project milestones sit last: the three above are client-wide 
      <Row className="g-3 mb-3">
        <Col md={12}>
          <RemediationTrajectoryChart series={filteredTrajectorySeries} />
        </Col>
      </Row>*/}

      {/* Recommended actions */}
      {/* <RecommendedActions actions={recommendations} /> */}
    </div>
  );
};

export default DashboardPage;
